import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';

/* PUBLIC PAGE — no auth — route: /review/:token
   Add to App.jsx: <Route path="/review/:token" element={<ClientReviewPage />} />
   Add to vercel.json rewrites: { "source": "/(.*)", "destination": "/index.html" }
*/

const T = {
  bg:'#f8f9fb', surface:'#ffffff', border:'#e8e4f3',
  purple:'#7c3aed', purpleBg:'#ede9fe',
  text:'#1a1035', textMd:'#4a3f6b', textSm:'#8b7fb0',
  green:'#059669', greenBg:'#d1fae5',
  font:"'Poppins', sans-serif",
  shadow:'0 4px 20px rgba(0,0,0,0.08)',
};

const API = (import.meta.env.VITE_API_URL || 'https://reach360-software.onrender.com/api');

export default function ClientReviewPage() {
  const { token } = useParams();
  const [task, setTask] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null);
  const [reworkNotes, setReworkNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!token) { setErr('Invalid review link'); setLoading(false); return; }
    fetch(`${API}/tasks/review/${token}`)
      .then(r => r.json())
      .then(d => { if (d.error) { setErr(d.error); } else { setTask(d); } setLoading(false); })
      .catch(() => { setErr('Failed to load. Please try again.'); setLoading(false); });
  }, [token]);

  const submit = async () => {
    if (action === 'rework' && !reworkNotes.trim()) return alert('Please describe what changes are needed.');
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/tasks/review/${token}/respond`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, notes: reworkNotes}),
      });
      const d = await r.json();
      if (d.error) { alert(d.error); setSubmitting(false); return; }
      setDone(action);
    } catch { alert('Submission failed. Please try again.'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <Center><Spin/><p style={{color:T.textSm,fontSize:14,marginTop:16}}>Loading your review...</p></Center>;
  if (err) return <Center><div style={{background:T.surface,borderRadius:16,padding:'32px 28px',maxWidth:420,width:'100%',textAlign:'center',boxShadow:T.shadow}}><div style={{fontSize:40,marginBottom:16}}>🔗</div><div style={{fontWeight:700,fontSize:18,color:T.text,marginBottom:8}}>Link Not Found</div><div style={{fontSize:14,color:T.textSm,lineHeight:1.6}}>{err}</div><div style={{marginTop:16,fontSize:12,color:T.textSm}}>Contact Reach 360 · 9443394439</div></div></Center>;

  if (done === 'rework') return <Center bg="#fff7ed"><div style={{background:T.surface,borderRadius:16,padding:'40px 32px',maxWidth:440,width:'100%',textAlign:'center',boxShadow:T.shadow}}><div style={{fontSize:56,marginBottom:16}}>🔄</div><div style={{fontWeight:800,fontSize:22,color:'#d97706',marginBottom:8}}>Feedback Sent!</div><div style={{fontSize:14,color:T.textMd,lineHeight:1.7}}>Thank you for your feedback!<br/>We'll get back to you with the updated version.</div><div style={{background:'#fef3c7',borderRadius:10,padding:'12px 16px',marginTop:16,fontSize:12,color:'#92400e',textAlign:'left'}}><strong>Your feedback:</strong><br/>{reworkNotes}</div><div style={{marginTop:20,fontSize:12,color:T.textSm}}>Reach 360 · 9443394439</div></div></Center>;

  return (
    <div style={{minHeight:'100vh',background:T.bg,fontFamily:T.font}}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}*{box-sizing:border-box}`}</style>

      {/* Header */}
      <div style={{background:'#1a1035',padding:'14px 24px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
        <div style={{display:'flex',alignItems:'center',gap:12}}>
          <img src="/reach-360.svg" alt="Reach 360" style={{height:34}} onError={e=>e.target.style.display='none'}/>
          <div>
            <div style={{fontWeight:700,fontSize:15,color:'#fff',letterSpacing:0.5}}>REACH 360</div>
            <div style={{fontSize:9,color:'rgba(255,255,255,0.45)',letterSpacing:1.2}}>MEDIA BRANDING COMPANY</div>
          </div>
        </div>
        <div style={{fontSize:11,color:'rgba(255,255,255,0.35)'}}>Content Review</div>
      </div>

      <div style={{maxWidth:760,margin:'0 auto',padding:'24px 16px'}}>

        {/* Task info card */}
        <div style={{background:T.surface,borderRadius:14,padding:'18px 20px',marginBottom:18,boxShadow:T.shadow}}>
          <div style={{fontSize:10,fontWeight:700,color:T.purple,letterSpacing:1,textTransform:'uppercase',marginBottom:5}}>{task?.client?.company_name||task?.client_name} · Content Review</div>
          <div style={{fontWeight:700,fontSize:19,color:T.text,marginBottom:8}}>{task?.content_description}</div>
          <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
            {task?.content_type&&<span style={{background:T.purpleBg,color:T.purple,fontSize:11,fontWeight:600,padding:'3px 10px',borderRadius:20}}>{task.content_type.name}</span>}
            {task?.rework_count>0&&<span style={{background:'#ffedd5',color:'#ea580c',fontSize:11,fontWeight:600,padding:'3px 10px',borderRadius:20}}>Revision #{task.rework_count}</span>}
          </div>
        </div>

        {/* Video — always show, even after approved */}
        {task?.edited_video&&(
          <div style={{background:'#000',borderRadius:14,overflow:'hidden',marginBottom:18,boxShadow:T.shadow}}>
            {task.is_bunny&&task.video_url ? (
              <iframe src={task.video_url} style={{width:'100%',aspectRatio:'16/9',border:'none',display:'block'}} allow="autoplay; fullscreen" allowFullScreen title="content review"/>
            ) : task.video_url ? (
              <video src={task.video_url} controls style={{width:'100%',aspectRatio:'16/9',display:'block',background:'#000'}} playsInline controlsList="nodownload"/>
            ) : (
              <div style={{aspectRatio:'16/9',display:'flex',alignItems:'center',justifyContent:'center',background:'#111'}}>
                <div style={{textAlign:'center',color:'#666'}}><div style={{fontSize:36,marginBottom:8}}>🎬</div><div style={{fontSize:13}}>Video not available</div><div style={{fontSize:11,marginTop:4}}>Contact Reach 360 · 9443394439</div></div>
              </div>
            )}
            
          </div>
        )}

        {/* Download button — always visible when video exists */}
        
                        {task?.edited_video&&(
          
            <a href={`${API}/tasks/review/${token}/download`}
            style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8,width:'100%',background:'#1a1035',color:'#fff',borderRadius:10,padding:'13px',fontSize:14,fontWeight:700,textDecoration:'none',boxSizing:'border-box',fontFamily:T.font,marginBottom:18}}
          >
            Download Video
          </a>
        )}

        {/* Approved status bar */}
        {(done==='approved'||task?.client_approval_status==='approved')&&(
          <div style={{background:'#f0fdf4',border:'2px solid #86efac',borderRadius:12,padding:'14px 18px',marginBottom:18,display:'flex',alignItems:'center',gap:12,boxShadow:T.shadow}}>
            <div style={{fontSize:28,flexShrink:0}}>✅</div>
            <div>
              <div style={{fontWeight:700,fontSize:14,color:'#065f46'}}>Content Approved</div>
              <div style={{fontSize:12,color:'#15803d',marginTop:2}}>
                {task?.client_approved_at
                  ? `Approved on ${new Date(task.client_approved_at).toLocaleDateString('en-IN',{day:'numeric',month:'long',year:'numeric'})}`
                  : 'Your approval has been recorded. Reach 360 team has been notified.'}
              </div>
            </div>
          </div>
        )}

        {(task?.cta === 'yes' || task?.cta_link) && task?.cta_link && (
          <div style={{
            background:'#f0fdf4',
            border:'1.5px solid #86efac',
            borderRadius:12,
            padding:'14px 18px',
            marginBottom:18,
            display:'flex',
            alignItems:'center',
            justifyContent:'space-between',
            gap:12,
            flexWrap:'wrap',
            boxShadow:T.shadow
          }}>
            <div>
              <div style={{fontWeight:700,fontSize:13,color:'#166534',marginBottom:3}}>
                📎 Supporting Document / CTA
              </div>
              <div style={{fontSize:12,color:'#15803d',lineHeight:1.5}}>
                Reference material attached for this content
              </div>
            </div>
            
            <a href={task.cta_link}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                background:'#16a34a',
                color:'#fff',
                borderRadius:9,
                padding:'9px 18px',
                fontSize:13,
                fontWeight:600,
                textDecoration:'none',
                display:'inline-flex',
                alignItems:'center',
                gap:6,
                whiteSpace:'nowrap',
                flexShrink:0
              }}
            >
              🔗 Open Document
            </a>
          </div>
        )}


        {task?.client_approval_notes&&(
          <div style={{background:'#fff7ed',border:'1px solid #fed7aa',borderRadius:12,padding:'13px 16px',marginBottom:18}}>
            <div style={{fontWeight:600,fontSize:12,color:'#9a3412',marginBottom:5}}>Your Previous Feedback:</div>
            <div style={{fontSize:13,color:'#92400e',lineHeight:1.6}}>{task.client_approval_notes}</div>
          </div>
        )}

        {/* Action */}
                {!action&&task?.client_approval_status!=='approved'&&done!=='approved' ? (
          <div style={{background:T.surface,borderRadius:14,padding:'22px 20px',boxShadow:T.shadow}}>
            <div style={{fontWeight:700,fontSize:16,color:T.text,marginBottom:5}}>What would you like to do?</div>
            <div style={{fontSize:13,color:T.textSm,marginBottom:18}}>Please watch the full video above before deciding.</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
              <button onClick={()=>setAction('approved')} style={{background:T.greenBg,border:`2px solid ${T.green}`,borderRadius:12,padding:'18px 14px',cursor:'pointer',fontFamily:T.font,textAlign:'center'}}>
                <div style={{fontSize:26,marginBottom:8}}>✅</div>
                <div style={{fontWeight:700,fontSize:14,color:T.green}}>Approve</div>
                <div style={{fontSize:11,color:'#065f46',marginTop:4}}>I'm satisfied with this</div>
              </button>
              <button onClick={()=>setAction('rework')} style={{background:'#fff7ed',border:'2px solid #fed7aa',borderRadius:12,padding:'18px 14px',cursor:'pointer',fontFamily:T.font,textAlign:'center'}}>
                <div style={{fontSize:26,marginBottom:8}}>🔄</div>
                <div style={{fontWeight:700,fontSize:14,color:'#d97706'}}>Request Changes</div>
                <div style={{fontSize:11,color:'#92400e',marginTop:4}}>I need modifications</div>
              </button>
            </div>
          </div>
               ) : action ? (
          <div style={{background:T.surface,borderRadius:14,padding:'22px 20px',boxShadow:T.shadow}}>
            <button onClick={()=>setAction(null)} style={{background:'none',border:'none',cursor:'pointer',color:T.purple,fontFamily:T.font,fontSize:12,fontWeight:600,marginBottom:16,padding:0}}>← Back</button>
            {action==='approve'&&<><div style={{fontWeight:700,fontSize:16,color:T.green,marginBottom:5}}>✅ Confirm Approval</div><div style={{fontSize:13,color:T.textSm,marginBottom:18}}>Confirm you're happy with this content.</div></>}
            {action==='rework'&&(
              <>
                <div style={{fontWeight:700,fontSize:16,color:'#d97706',marginBottom:5}}>🔄 Request Changes</div>
                <div style={{fontSize:13,color:T.textSm,marginBottom:14}}>Please describe exactly what changes you'd like.</div>
                <div style={{marginBottom:16}}>
                  <label style={{display:'block',fontWeight:600,fontSize:12,color:T.textMd,marginBottom:5}}>What changes are needed? *</label>
                  <textarea value={reworkNotes} onChange={e=>setReworkNotes(e.target.value)} rows={4} placeholder="e.g. Please change the background music, fix the text at 0:23, add logo at the end..." style={{width:'100%',border:`1.5px solid ${T.border}`,borderRadius:10,padding:'10px 12px',fontSize:13,fontFamily:T.font,color:T.text,background:'#faf9ff',outline:'none',resize:'vertical',lineHeight:1.7}}/>
                </div>
              </>
            )}
            <button onClick={submit} disabled={submitting} style={{width:'100%',background:action==='approve'?T.green:'#d97706',color:'#fff',border:'none',borderRadius:10,padding:'14px',fontSize:14,fontWeight:700,cursor:submitting?'not-allowed':'pointer',fontFamily:T.font,opacity:submitting?0.7:1}}>
              {submitting?'Submitting...':(action==='approved'?'✅ Confirm Approval':'🔄 Submit Feedback')}
            </button>
          </div>
        ) : null}
        <div style={{textAlign:'center',marginTop:24,fontSize:11,color:T.textSm}}>
          Reach 360 · 9443394439 · reach360d@gmail.com · www.reach360.in
        </div>
      </div>
    </div>
  );
}

const Center = ({children, bg='#f8f9fb'}) => (
  <div style={{minHeight:'100vh',background:bg,display:'flex',alignItems:'center',justifyContent:'center',fontFamily:"'Poppins',sans-serif",padding:20}}>
    {children}
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>
);

const Spin = () => <div style={{width:36,height:36,border:'3px solid #ede9fe',borderTop:'3px solid #7c3aed',borderRadius:'50%',animation:'spin 0.8s linear infinite',margin:'0 auto'}}/>;